import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { calculatorCategories } from '@/data/calculatorRegistry';
import { ChevronDown, ChevronRight, Home, BookOpen } from 'lucide-react';
import GlobeLogo from '@/components/shared/GlobeLogo';
import SmartSearch from '@/components/shared/SmartSearch';

function SidebarContent({ activeCalc, onSelectCalc, onGoHome, onSelectCategory, onAfterNavigate }) {
  // Find which category the active calculator belongs to
  const activeCatId = calculatorCategories.find(c =>
    c.calculators.some(calc => calc.id === activeCalc)
  )?.id || null;

  // Collapsed by default — only the active category opens automatically
  const [openCats, setOpenCats] = useState(() =>
    calculatorCategories.reduce((a, c) => ({ ...a, [c.id]: c.id === activeCatId }), {})
  );

  // Auto-expand the category when activeCalc changes (e.g. via search or deep link)
  useEffect(() => {
    if (activeCatId) {
      setOpenCats(prev => ({ ...prev, [activeCatId]: true }));
    }
  }, [activeCatId]);

  const toggle = (catId) => setOpenCats(p => ({ ...p, [catId]: !p[catId] }));

  return (
    <div className="flex flex-col h-full bg-card text-foreground border-r" role="navigation" aria-label="Calculator categories">

      {/* Logo */}
      <div className="p-4 pb-2 cursor-pointer shrink-0" onClick={onGoHome} data-testid="sidebar-logo">
        <div className="flex items-center gap-3">
          <GlobeLogo size={44} testid="sidebar-globe-logo" />
          <div className="text-xl font-bold font-chivo tracking-tight leading-none">
            <span className="text-foreground">Calc</span>
            <span className="text-[#1A8FE3]">Globe</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="px-3 pb-2 pt-1 shrink-0">
        <SmartSearch variant="plain" testidPrefix="sidebar-search" onResultSelected={() => onAfterNavigate && onAfterNavigate()} />
      </div>

      {/* Home button */}
      <div className="px-3 pb-2 shrink-0">
        <button
          onClick={() => { onGoHome(); onAfterNavigate && onAfterNavigate(); }}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
          data-testid="sidebar-home-btn"
        >
          <Home className="w-4 h-4 text-[#1A8FE3] shrink-0" />
          Home
        </button>
      </div>

      {/* Divider + label */}
      <div className="px-4 pb-1.5 shrink-0">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">Categories</p>
      </div>

      {/* Category list */}
      <ScrollArea className="flex-1 px-2">
        <div className="pb-4 space-y-0.5">
          {calculatorCategories.map(cat => {
            const Icon = cat.icon;
            const isOpen = openCats[cat.id];
            const isActiveCat = cat.id === activeCatId;

            return (
              <div key={cat.id}>
                {/* Category header row */}
                <div className={cn(
                  'flex items-center w-full rounded-md transition-colors group',
                  isActiveCat ? 'bg-[#1A8FE3]/8' : 'hover:bg-muted/60'
                )}>
                  {/* Category name — navigates to category page */}
                  <button
                    onClick={() => {
                      onSelectCategory ? onSelectCategory(cat.id) : onSelectCalc(cat.calculators[0].id);
                      onAfterNavigate && onAfterNavigate();
                    }}
                    className="flex items-center flex-1 min-w-0 px-2.5 py-2.5 text-left gap-2.5"
                    data-testid={`cat-${cat.id}`}
                    aria-label={`${cat.name} category`}
                  >
                    <div className={cn(
                      'w-6 h-6 rounded-md flex items-center justify-center shrink-0',
                      isActiveCat ? 'bg-[#1A8FE3] text-white' : 'bg-[#1A8FE3]/10 text-[#1A8FE3]'
                    )}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <span className={cn(
                      'flex-1 text-left text-[13px] font-semibold truncate',
                      isActiveCat ? 'text-[#1A8FE3]' : 'text-foreground/85'
                    )}>
                      {cat.name}
                    </span>
                    {/* Tool count badge */}
                    <span className="text-[10px] font-mono text-muted-foreground/60 shrink-0 mr-1">
                      {cat.calculators.length}
                    </span>
                  </button>

                  {/* Expand/collapse toggle */}
                  <button
                    onClick={() => toggle(cat.id)}
                    className="p-1.5 mr-1 rounded hover:bg-muted transition-colors shrink-0"
                    aria-label={isOpen ? `Collapse ${cat.name}` : `Expand ${cat.name}`}
                    data-testid={`cat-toggle-${cat.id}`}
                  >
                    {isOpen
                      ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                      : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                    }
                  </button>
                </div>

                {/* Calculator list — only when expanded */}
                {isOpen && (
                  <div className="ml-3 pl-3 border-l border-border/50 mt-0.5 mb-1 space-y-0.5">
                    {cat.calculators.map(calc => (
                      <button
                        key={calc.id}
                        onClick={() => { onSelectCalc(calc.id); onAfterNavigate && onAfterNavigate(); }}
                        className={cn(
                          'w-full text-left px-2.5 py-1.5 text-[12px] rounded-md transition-colors',
                          activeCalc === calc.id
                            ? 'bg-[#1A8FE3]/15 text-[#1A8FE3] font-semibold'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        )}
                        data-testid={`calc-link-${calc.id}`}
                      >
                        {calc.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Footer — Blog link */}
      <div className="p-3 border-t border-border shrink-0">
        <a
          href="https://blog.calcglobe.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium text-muted-foreground hover:text-[#1A8FE3] hover:bg-[#1A8FE3]/5 transition-colors"
          data-testid="sidebar-blog-link"
        >
          <BookOpen className="w-3.5 h-3.5 shrink-0" />
          Read the CalcGlobe Blog
        </a>
      </div>
    </div>
  );
}

export default function Sidebar({ activeCalc, onSelectCalc, onGoHome, isOpen, onClose, onSelectCategory }) {
  const props = { activeCalc, onSelectCalc, onGoHome, onSelectCategory };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="left" className="w-[280px] p-0 border-0" data-testid="mobile-sidebar">
          <VisuallyHidden.Root>
            <SheetTitle>Calculator Categories</SheetTitle>
            <SheetDescription>Browse all calculator categories and tools</SheetDescription>
          </VisuallyHidden.Root>
          <SidebarContent
            {...props}
            onSelectCalc={(id) => { onSelectCalc(id); onClose(); }}
            onSelectCategory={(id) => { onSelectCategory && onSelectCategory(id); onClose(); }}
            onGoHome={() => { onGoHome(); onClose(); }}
            onAfterNavigate={() => onClose()}
          />
        </SheetContent>
      </Sheet>
    </>
  );
}
